import type { DailyNotificationEvent } from "../disciplio/daily-notification-utils";

export type BrowserNotificationState = "unsupported" | "default" | "granted" | "denied";

export type BrowserPushSubscription = {
  endpoint: string;
  expirationTime: number | null;
  keys: { p256dh: string; auth: string };
};

export type BrowserPushSetupErrorCode =
  | "unsupported"
  | "permission_denied"
  | "service_worker_unavailable"
  | "config_unavailable"
  | "invalid_configuration"
  | "subscribe_failed"
  | "serialize_failed"
  | "save_failed";

export class BrowserPushSetupError extends Error {
  readonly code: BrowserPushSetupErrorCode;
  readonly cause?: unknown;

  constructor(code: BrowserPushSetupErrorCode, message: string, cause?: unknown) {
    super(message);
    this.name = "BrowserPushSetupError";
    this.code = code;
    this.cause = cause;
  }
}

type SetupStage =
  | "config"
  | "permission"
  | "service_worker"
  | "existing_subscription"
  | "subscribe"
  | "serialize"
  | "persist";

function logPushStage(stage: SetupStage, detail: Record<string, unknown> = {}) {
  console.info("[disciplio:web-push]", stage, detail);
}

function logPushFailure(stage: SetupStage, error: unknown) {
  if (error instanceof DOMException) {
    console.error("[disciplio:web-push]", stage, { name: error.name, message: error.message });
    return;
  }
  if (error instanceof Error) {
    console.error("[disciplio:web-push]", stage, { name: error.name, message: error.message });
    return;
  }
  console.error("[disciplio:web-push]", stage, { message: String(error) });
}

export function browserNotificationState(): BrowserNotificationState {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  return Notification.permission;
}

/** True when the user is actively looking at a Disciplio tab. */
export function isDisciplioPageFocused(): boolean {
  if (typeof document === "undefined") return false;
  return document.visibilityState === "visible" && document.hasFocus();
}

export function supportsBrowserPush() {
  return typeof window !== "undefined"
    && "Notification" in window
    && "serviceWorker" in navigator
    && "PushManager" in window;
}

export function isAppleMobileBrowser() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export function isStandaloneWebApp() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches === true
    || (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function needsAppleHomeScreenInstall() {
  return isAppleMobileBrowser() && !isStandaloneWebApp();
}

export async function registerNotificationServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  try {
    return await ensureNotificationServiceWorker();
  } catch {
    return null;
  }
}

export async function ensureNotificationServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    throw new BrowserPushSetupError("service_worker_unavailable", "Service worker unavailable");
  }
  try {
    logPushStage("service_worker", { action: "register" });
    await navigator.serviceWorker.register("/notification-service-worker.js", {
      scope: "/",
      updateViaCache: "none",
    });
    const ready = await navigator.serviceWorker.ready;
    if (!ready?.active && !ready?.pushManager) {
      throw new BrowserPushSetupError("service_worker_unavailable", "Service worker is not ready");
    }
    logPushStage("service_worker", { action: "ready", scriptURL: ready.active?.scriptURL ?? null });
    return ready;
  } catch (error) {
    logPushFailure("service_worker", error);
    if (error instanceof BrowserPushSetupError) throw error;
    throw new BrowserPushSetupError("service_worker_unavailable", "Service worker unavailable", error);
  }
}

export async function requestBrowserNotificationPermission(): Promise<BrowserNotificationState> {
  if (browserNotificationState() === "unsupported") return "unsupported";
  if (Notification.permission !== "default") return Notification.permission;
  try {
    logPushStage("permission", { action: "request" });
    return await Notification.requestPermission();
  } catch (error) {
    logPushFailure("permission", error);
    return browserNotificationState();
  }
}

/** Convert a base64url VAPID public key into a detached Uint8Array for PushManager.subscribe. */
export function vapidPublicKeyToBytes(publicKey: string): Uint8Array {
  const trimmed = publicKey.trim();
  if (!trimmed || /BEGIN|END|-----/.test(trimmed) || /\s/.test(trimmed)) {
    throw new BrowserPushSetupError("invalid_configuration", "Invalid notification configuration");
  }
  if (!/^[A-Za-z0-9_-]+$/.test(trimmed)) {
    throw new BrowserPushSetupError("invalid_configuration", "Invalid notification configuration");
  }
  try {
    const padded = trimmed.replace(/-/g, "+").replace(/_/g, "/");
    const padLength = (4 - (padded.length % 4)) % 4;
    const binary = atob(padded + "=".repeat(padLength));
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    // Chrome requires an ArrayBuffer whose byteLength matches the key exactly.
    return bytes.slice();
  } catch (error) {
    throw new BrowserPushSetupError("invalid_configuration", "Invalid notification configuration", error);
  }
}

function subscriptionToPayload(subscription: PushSubscription): BrowserPushSubscription {
  const json = subscription.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!json.endpoint || !p256dh || !auth) {
    throw new BrowserPushSetupError("serialize_failed", "Subscription could not be serialized");
  }
  return {
    endpoint: json.endpoint,
    expirationTime: json.expirationTime ?? null,
    keys: { p256dh, auth },
  };
}

function bufferSourcesEqual(left: BufferSource, right: BufferSource): boolean {
  const a = toUint8Array(left);
  const b = toUint8Array(right);
  if (a.byteLength !== b.byteLength) return false;
  for (let index = 0; index < a.byteLength; index += 1) {
    if (a[index] !== b[index]) return false;
  }
  return true;
}

function toUint8Array(value: BufferSource): Uint8Array {
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
}

function endpointHost(endpoint: string): string | null {
  try {
    return new URL(endpoint).host;
  } catch {
    return null;
  }
}

export async function currentBrowserPushSubscription(): Promise<BrowserPushSubscription | null> {
  if (!supportsBrowserPush()) return null;
  try {
    const registration = await ensureNotificationServiceWorker();
    const subscription = await registration.pushManager.getSubscription();
    return subscription ? subscriptionToPayload(subscription) : null;
  } catch {
    return null;
  }
}

export async function subscribeBrowserPush(publicKey: string): Promise<BrowserPushSubscription> {
  if (!supportsBrowserPush()) {
    throw new BrowserPushSetupError("unsupported", "Browser notifications are not supported");
  }
  if (Notification.permission !== "granted") {
    throw new BrowserPushSetupError("permission_denied", "Notification permission is required");
  }
  if (!publicKey.trim()) {
    throw new BrowserPushSetupError("config_unavailable", "Browser push is not configured");
  }

  const applicationServerKey = vapidPublicKeyToBytes(publicKey);
  const registration = await ensureNotificationServiceWorker();

  logPushStage("existing_subscription", { action: "lookup" });
  const current = await registration.pushManager.getSubscription();
  if (current) {
    const currentKey = current.options.applicationServerKey;
    const sameKey = Boolean(currentKey && bufferSourcesEqual(currentKey, applicationServerKey));
    logPushStage("existing_subscription", {
      found: true,
      host: endpointHost(current.endpoint),
      sameKey,
    });
    if (sameKey) {
      try {
        return subscriptionToPayload(current);
      } catch (error) {
        logPushFailure("serialize", error);
        await current.unsubscribe().catch(() => false);
      }
    } else {
      await current.unsubscribe().catch((error) => {
        logPushFailure("existing_subscription", error);
        return false;
      });
    }
  } else {
    logPushStage("existing_subscription", { found: false });
  }

  try {
    logPushStage("subscribe", { action: "pushManager.subscribe" });
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
    logPushStage("serialize", { host: endpointHost(subscription.endpoint) });
    return subscriptionToPayload(subscription);
  } catch (error) {
    logPushFailure("subscribe", error);
    if (error instanceof BrowserPushSetupError) throw error;
    throw new BrowserPushSetupError("subscribe_failed", "Browser subscription creation failed", error);
  }
}

/**
 * Full Account enablement path: permission → subscribe → caller persists.
 * Does not treat Notification.permission === "granted" as an active subscription.
 */
export async function createOrRefreshBrowserPushSubscription(publicKey: string): Promise<{
  permission: BrowserNotificationState;
  subscription: BrowserPushSubscription;
  replaced: boolean;
  previousEndpoint: string | null;
}> {
  if (!supportsBrowserPush()) {
    throw new BrowserPushSetupError("unsupported", "Browser notifications are not supported");
  }

  const permission = await requestBrowserNotificationPermission();
  if (permission !== "granted") {
    throw new BrowserPushSetupError("permission_denied", "Notification permission is required");
  }

  let replaced = false;
  let previousEndpoint: string | null = null;
  try {
    const registration = await ensureNotificationServiceWorker();
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      previousEndpoint = existing.endpoint;
      const applicationServerKey = vapidPublicKeyToBytes(publicKey);
      const currentKey = existing.options.applicationServerKey;
      if (!(currentKey && bufferSourcesEqual(currentKey, applicationServerKey))) {
        replaced = true;
      }
    }
  } catch {
    // subscribeBrowserPush will surface readiness errors.
  }

  const subscription = await subscribeBrowserPush(publicKey);
  if (!replaced) previousEndpoint = null;
  return { permission, subscription, replaced, previousEndpoint };
}

export async function unsubscribeBrowserPush(): Promise<BrowserPushSubscription | null> {
  if (!supportsBrowserPush()) return null;
  try {
    const registration = await ensureNotificationServiceWorker();
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return null;
    const payload = subscriptionToPayload(subscription);
    await subscription.unsubscribe().catch(() => false);
    return payload;
  } catch {
    return null;
  }
}

export async function showBrowserNotification(
  event: Pick<DailyNotificationEvent, "id" | "kind" | "scheduledTime" | "itemTitle">,
  endLeadMinutes: number,
): Promise<void> {
  if (browserNotificationState() !== "granted") return;
  const body = event.kind === "START"
    ? `Start now · ${event.scheduledTime}`
    : `Ends in ${endLeadMinutes} min · ${event.scheduledTime}`;
  await showSystemNotification({
    title: "Disciplio",
    body: `${body}\n${event.itemTitle}`,
    tag: event.id,
    url: "/",
  });
}

export async function showSystemNotification(options: {
  title: string;
  body: string;
  tag?: string;
  url?: string;
}): Promise<boolean> {
  if (browserNotificationState() !== "granted") return false;
  const notificationOptions: NotificationOptions & { renotify?: boolean } = {
    body: options.body,
    icon: "/assets/logo-transparent.png",
    badge: "/favicon.svg",
    tag: options.tag || "disciplio-reminder",
    renotify: true,
    requireInteraction: false,
    data: { url: options.url || "/" },
  };
  try {
    const registration = await registerNotificationServiceWorker();
    if (registration) {
      await registration.showNotification(options.title, notificationOptions);
      return true;
    }
    new Notification(options.title, notificationOptions);
    return true;
  } catch {
    return false;
  }
}
